import { useCallback } from "react";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useDispatch, useSelector } from "react-redux";
import { setProposalId, setTypeModal } from "../../features/gms-state-and-modal/modal-slice";
import { selectExistingCsvFilePath } from "../../features/gms-proposal-forms/types-proposal-product-basic-plan/types-proposal-product-basic-plan-slice";
import { useToastify } from "../../library/toastify/toast-provider";
import { useDownloadCOCAsPDFMutation, useDownloadFileDocumentsMutation, useDownloadMasterFileCSVFileMutation, useDownloadProposalPDFMutation, useDownloadSupportingDocumentsMutation, useLazyViewCOCAsPDFQuery, useLazyViewPolicyQuery, useLazyViewProposalPDFQuery, useViewPrototypeTemplatePFDMutation } from "../../api/files/files-api";
import { selectedCurrentProposalStatusName, setProposalExtension } from "../../features/gms-state-and-modal/state-slice";
import { useProposalTypeData } from "../proposal/use-proposal-type-data";
import { setRatesTabs } from "../../features/actuarial/rates-slice";
import { useModalAccess } from "../../components/global/modal/use-modal-access";

export const useFilesActions = () => {

    const { showToastNotify } = useToastify();
    const dispatch = useDispatch();
    const { 
        isDraftProposal,
        groupName,
        product,
        prototype,
    } = useProposalTypeData()
    const { currentProposalId } = useModalAccess();
    const currentProposalStatus = useSelector(selectedCurrentProposalStatusName);
    const filePathName = useSelector(selectExistingCsvFilePath)

    const [downloadProposalPDF, { isLoading: isLoadingDownloadProposalPDF, }] = useDownloadProposalPDFMutation();
    const [viewProposal, { isFetching: isLoadingViewPDFProposal, }] = useLazyViewProposalPDFQuery();

    const [downloadMasterAttachedFile, { isLoading: isLoadingDownloadMasterAttachedFile }] = useDownloadMasterFileCSVFileMutation();
    const [downloadSupportingDocuments, { isLoading: isLoadingDownloadSupportingDocuments }] = useDownloadSupportingDocumentsMutation();
    const [downloadActDocuments, { isLoading: isLoadingDownloadActDocuments }] = useDownloadFileDocumentsMutation();
    const [viewPrototypeTemplate, { isLoading: isLoadingViewPrototypeTemplate }] = useViewPrototypeTemplatePFDMutation();

    const [generateCOC, { isLoading: isLoadingGenerateCOC, }] = useDownloadCOCAsPDFMutation();
    const [viewCOC, { isFetching: isLoadingViewCOC, }] = useLazyViewCOCAsPDFQuery();
    const [viewPolicy, { isFetching: isLoadingViewPolicy, }] = useLazyViewPolicyQuery();

    const handleViewProposal = (id: number, preparedById: number, status: string) => {
        // dispatch(setTyeOfProposalStatus(status))
        dispatch(setProposalId(id));
        dispatch(setTypeModal('showActionProposalModal'))
        dispatch(setRatesTabs('info'))
    };
    
    const handleViewProposalExtension = (id: number, secName: string, preparedBy: string) => {
        dispatch(setProposalId(id));
        // dispatch(setTypeOfUserView('MRK_GMS_RH'));
        dispatch(setTypeModal('showActionProposalExtension'))
        dispatch(setProposalExtension({ type: 'prep', value: preparedBy }))
        dispatch(setProposalExtension({ type: 'sec', value: secName }))
        // dispatch(setRatesTabs('info'))
    }

    const handleViewProposalPDF = async () => {
        if (!currentProposalId) return;
        if (!currentProposalStatus) return;
        
        if(isDraftProposal) {
            showToastNotify("You can't view the proposal in draft status.", 'warning')
            return
        }
        
        try {
            const url = await viewProposal({ proposalID: currentProposalId }).unwrap();
            window.open(url, "_blank");
            const cleanup = () => URL.revokeObjectURL(url);
            window.addEventListener("beforeunload", cleanup);
            setTimeout(cleanup, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    const handleDownloadProposalPDF = async () => {
        const productName = product?.name || prototype?.name;
        
        if (!currentProposalId) return;
        if (!currentProposalStatus) return;
        if (!groupName) return;
        if (!productName) return;

        if(isDraftProposal) {
            showToastNotify(`You can't download the proposal in ${currentProposalStatus} status.`, 'warning');
            return
        }

        try {
            const fileName = `${groupName} - ${productName}.pdf`;
            const url = await downloadProposalPDF({ proposalID: currentProposalId }).unwrap();

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();

            const cleanup = () => URL.revokeObjectURL(url);
            window.addEventListener("beforeunload", cleanup);
            setTimeout(cleanup, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };


    const handleDownloadMasterFile = useCallback(async () => {
        try {
            if (!currentProposalId || !filePathName) return showToastNotify('No attached file.', 'warning');
            const blobUrl = await downloadMasterAttachedFile({proposalID: currentProposalId}).unwrap();
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filePathName;

            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            const errorMsg1 = error?.message
            const errorMsg2 = getErrorMessage(errorMsg1);
            const fileErrorMsg = errorMsg1 || errorMsg2
            showToastNotify(fileErrorMsg, 'error')
        }
    }, [currentProposalId, filePathName, downloadMasterAttachedFile, showToastNotify]);

    const handleDownloadSupportingDocumentsCFE = async (file_path: string, fileName: string) => {
        if(!currentProposalId || !file_path || !fileName) return
        try {
            
            const blobUrl = await downloadSupportingDocuments({
                proposalID: currentProposalId, file_path,
            }).unwrap();

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            link.remove();

            // cleanup
            URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            const errorMsg1 = error?.message
            const errorMsg2 = getErrorMessage(errorMsg1);
            const fileErrorMsg = errorMsg1 || errorMsg2
            showToastNotify(fileErrorMsg, 'error')
        }
    }

    const handleDownloadSupportingDocumentsAct = async (file_path: string, fileName: string) => {
        if(!currentProposalId || !file_path || !fileName) return
        try {
            
            const blobUrl = await downloadActDocuments({
                proposalID: currentProposalId, file_path,
            }).unwrap();

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();
            link.remove();

            // cleanup
            URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            const errorMsg1 = error?.message
            const errorMsg2 = getErrorMessage(errorMsg1);
            const fileErrorMsg = errorMsg1 || errorMsg2
            showToastNotify(fileErrorMsg, 'error')
        }
    }

    const handleViewPrototypeTemplate = async (prototypeId: number) => {
        if (!prototypeId) return;
        try {
            const url = await viewPrototypeTemplate({prototypeId}).unwrap();
            window.open(url, "_blank");
            const cleanup = () => URL.revokeObjectURL(url);
            window.addEventListener("beforeunload", cleanup);
            setTimeout(cleanup, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleViewCOC = async (value: number, userID?: number, status?: string) => {
        if (!value) return;
        try {
            const url = await viewCOC({ proposalID: value }).unwrap();
            window.open(url, "_blank");
            const cleanup = () => URL.revokeObjectURL(url);
            window.addEventListener("beforeunload", cleanup);
            setTimeout(cleanup, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }
    
    const handleViewPolicy = async (value: number) => {
        if (!value) return;
        try {
            const url = await viewPolicy({ policyId: value, signoff: false, watermark: false }).unwrap();
            window.open(url, "_blank");
            const cleanup = () => URL.revokeObjectURL(url);
            window.addEventListener("beforeunload", cleanup);
            setTimeout(cleanup, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleGenerateCOC = async (value: number, userID?: number, status?: string) => {
        if (!value) return;
        try {
            const url = await generateCOC({proposalID: value}).unwrap();
            const a = document.createElement("a");
            a.href = url;
            a.download = "";
            document.body.appendChild(a);
            a.click();
            a.remove();

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60000);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, "error");
        }
    }

    return {
        handleDownloadMasterFile,
        handleDownloadSupportingDocumentsCFE,
        handleDownloadSupportingDocumentsAct,
        handleViewPrototypeTemplate,
        isLoadingDownloadMasterAttachedFile,
        isLoadingDownloadSupportingDocuments,
        isLoadingDownloadActDocuments,
        isLoadingViewPrototypeTemplate,

        handleViewCOC,
        isLoadingViewCOC,
        handleGenerateCOC,
        isLoadingGenerateCOC,
        handleViewPolicy,
        isLoadingViewPolicy,

        handleViewProposalPDF,
        isLoadingViewPDFProposal,

        handleDownloadProposalPDF,
        isLoadingDownloadProposalPDF,

        handleViewProposal,
        handleViewProposalExtension,
    };
};